
import { Vue, Component } from 'vue-property-decorator';
import lazyInject from '@/di';
import SupplyService from '@/services/supply.service';
import { CProcurementGoods, CSupplierList } from '@/models/supply.model';

@Component
export default class GeneratePurchaseOrderModal extends Vue {
  visible = false;

  loading = false;

  tableData: any[] = [];

  // 供应商下拉数据源
  suppliers: CSupplierList[] = [];
  form = {
    supplierId: undefined,
  };

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  scrollY = 400;

  columns = [
    {
      title: '物料编码',
      dataIndex: 'commodity_code',
      key: 'commodity_code',
      width: '120',
      align: 'center',
    },
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      align: 'center',
    },
    {
      title: '包装规格',
      dataIndex: 'package_spec',
      key: 'packageSpec',
      width: '100',
      align: 'center',
    },
    {
      title: '数量/箱',
      dataIndex: 'per_package_num',
      key: 'per_package_num',
      width: '90',
      align: 'center',
    },
    {
      title: '升/壶',
      dataIndex: 'per_unit_num',
      key: 'per_unit_num',
      width: '90',
      align: 'center',
    },
    {
      title: '采购箱数',
      dataIndex: 'total_package_quantity',
      key: 'total_package_quantity',
      width: '90',
      align: 'center',
    },
    {
      title: '采购壶数',
      dataIndex: 'total_quantity',
      key: ' total_quantity',
      width: '90',
      align: 'center',
      // scopedSlots: { customRender: 'totalQuantity' },
    },
    {
      title: '采购单价/壶',
      dataIndex: 'platPurchasePrice',
      key: 'platPurchasePrice',
      width: '160',
      align: 'center',
      scopedSlots: { customRender: 'platPurchasePrice' },
    },
    {
      title: '总金额',
      dataIndex: 'platPurchaseAmount',
      key: 'platPurchaseAmount',
      width: 100,
      align: 'center',
      scopedSlots: { customRender: 'platPurchaseAmount' },
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: '120',
      align: 'center',
      scopedSlots: { customRender: 'action' },
    },
  ];

  // 合计金额 = 各行总金额之和
  get totalAmount() {
    return this.tableData
      .reduce(
        (total: number, record: any) => total + Number(this.calcAmount(record)),
        0,
      )
      .toFixed(2);
  }

  mounted() {
    window.addEventListener('resize', this.calcScrollY);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.calcScrollY);
  }

  // 纵向最大高度按当前页面高度动态计算：视口高度 - 弹窗头部/表头/合计栏/底部按钮栏等占用
  calcScrollY() {
    const offset = 400;
    this.scrollY = Math.max(window.innerHeight - offset, 200);
  }

  handleDelete(record: any) {
    const index = this.tableData.indexOf(record);
    if (index > -1) {
      this.tableData.splice(index, 1);
    }
  }

  // list：归集订单商品列表（接口原始数据），弹窗内按同一物料号汇总显示
  open(list: any[]) {
    this.loading = false;
    this.visible = true;
    this.form.supplierId = undefined;
    this.tableData = list || [];
    // this.tableData = this.aggregate(list || []);
    this.calcScrollY();
    this.getSuppliers();
  }

  onChange(value: any) {
    this.form.supplierId = value;
  }

  async getSuppliers(companyName = '') {
    const res: any = await this.supplyService.getSupplierList({
      status: 1,
      company_name: companyName,
    });
    if (res && res.list) {
      this.suppliers = res.list.map((item: any) => CSupplierList.from(item));
    } else {
      this.suppliers = [];
    }
  }

  // 供应商远程搜索：按 company_name 模糊匹配，空字符串时拉全量
  onSupplierSearch(companyName: string) {
    this.getSuppliers(companyName);
  }

  /** 供应商过滤（支持模糊搜索） */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supplierFilterOption(input: string, option: any) {
    return (
      option.componentOptions.children[0].text
        .toLowerCase()
        .indexOf(input.toLowerCase()) >= 0
    );
  }

  // 按物料编码汇总：箱数/壶数累加，采购单号去重合并
  aggregate(list: any[]) {
    const map: any = {};
    list.forEach((item) => {
      const goods = CProcurementGoods.from(item);
      const row = map[goods.commodityCode];
      if (!row) {
        map[goods.commodityCode] = {
          commodityCode: goods.commodityCode,
          commodityName: goods.commodityName,
          packageSpec: goods.packageSpec,
          perPackageNum: goods.perPackageNum,
          perUnitNum: goods.perUnitNum,
          orderNos: goods.orderNo ? [goods.orderNo] : [],
          packageQuantity: Number(goods.packageQuantity) || 0,
          quantity: Number(goods.quantity) || 0,
          platPurchasePrice: goods.platPurchasePrice
            ? Number(goods.platPurchasePrice)
            : undefined,
        };
        return;
      }
      row.packageQuantity += Number(goods.packageQuantity) || 0;
      row.quantity += Number(goods.quantity) || 0;
      if (goods.orderNo && row.orderNos.indexOf(goods.orderNo) === -1) {
        row.orderNos.push(goods.orderNo);
      }
    });
    return Object.keys(map).map((key) => map[key]);
  }

  // 总金额 = 采购壶数 * 采购单价/壶，保留 2 位小数
  calcAmount(record: any) {
    const price = Number(record.platPurchasePrice) || 0;
    const quantity = +record.total_quantity || 0;
    return (price * quantity).toFixed(2);
  }

  submit() {
    if (!this.form.supplierId) {
      this.$message.warning('请选择供应商');
      return;
    }
    if (!this.tableData.length) {
      this.$message.warning('暂无可生成的商品数据');
      return;
    }
    const invalidIndex = this.tableData.findIndex(
      (record) =>
        record.platPurchasePrice === undefined ||
        record.platPurchasePrice === null ||
        record.platPurchasePrice === '' ||
        !/^\d+(\.\d{1,2})?$/.test(String(record.platPurchasePrice)),
    );
    if (invalidIndex > -1) {
      this.$message.warning(
        `请填写第${invalidIndex + 1}行的采购单价/壶（最多两位小数）`,
      );
      return;
    }
    this.$emit('confirm', {
      supplier_no: this.form.supplierId,
      commodities: this.tableData,
    });
  }

  onClose() {
    this.visible = false;
    this.tableData = [];
    this.$nextTick(() => {
      this.form.supplierId = undefined;
    });
    this.loading = false;
  }
}
